import React, { useCallback, useMemo } from 'react';
import Select from 'react-select';

// Material UI
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';

// Data
import { DATE_OF_BIRTH_FALLBACK_FORM_OPTIONS, DATE_OF_BIRTH_FALLBACK_LABELS, SportTermLookup } from '../../../lib/types';
import { IFormOption } from '../../_core/_ui/forms.component';
import { getImportPlayersMessages } from './import-players.messages';

// Components
import { FieldCaption } from '../../_core/_ui/field-caption.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';

// defineMessages narrows each descriptor's id to a string literal, which IntlMsg requires;
// the plain MessageDescriptor type widens it back to string | number | undefined.
type ImportPlayersMessage = ReturnType<typeof getImportPlayersMessages>['captionMissingDobOnFile'];

const FIELD_ID = 'import-players-allow-missing-dob';
const CAPTION_ID = `${FIELD_ID}-caption`;
const FALLBACK_CAPTION_ID = `${FIELD_ID}-fallback-caption`;
const OVERWRITE_ID = 'import-players-overwrite-dob';
const OVERWRITE_CAPTION_ID = `${OVERWRITE_ID}-caption`;

interface IDateOfBirthOptionsFieldProps {
  termLookup: SportTermLookup;
  allowMissing: boolean;
  fallback: DateOfBirthFallback;
  overwrite: boolean;
  canOverwrite: boolean;
  setAllowMissing: (allowMissing: boolean) => void;
  setFallback: (fallback: DateOfBirthFallback) => void;
  setOverwrite: (overwrite: boolean) => void;
}

export const DateOfBirthOptionsField: React.FC<IDateOfBirthOptionsFieldProps> = ({
  termLookup,
  allowMissing,
  fallback,
  overwrite,
  canOverwrite,
  setAllowMissing,
  setFallback,
  setOverwrite,
}) => {
  const messages = getImportPlayersMessages(termLookup);

  // Each choice writes a real date onto every blank row, so the consequence is spelled
  // out per option rather than left to the label.
  const fallbackCaptions: Record<DateOfBirthFallback, ImportPlayersMessage> = {
    DATE_ON_FILE: messages.captionMissingDobOnFile,
    DEFAULT_DATE: messages.captionMissingDobDefault,
  };

  const selectedOption: IFormOption = useMemo(
    () => ({ value: fallback, label: DATE_OF_BIRTH_FALLBACK_LABELS[fallback] }),
    [fallback]
  );

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setAllowMissing(event.target.checked),
    [setAllowMissing]
  );

  const handleSelectFallback = useCallback(
    (option: IFormOption | null) => { if (option) setFallback(option.value as DateOfBirthFallback); },
    [setFallback]
  );

  const handleToggleOverwrite = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setOverwrite(event.target.checked),
    [setOverwrite]
  );

  return (
    <Box m={2}>
      <Paper>
        <Box p={2}>
          <FormControlLabel
            control={
              <Checkbox
                id={FIELD_ID}
                checked={allowMissing}
                onChange={handleToggle}
                inputProps={{ 'aria-describedby': CAPTION_ID }}
              />
            }
            label={<IntlMsg {...messages.lblAllowMissingDob} />}
          />
          <FieldCaption id={CAPTION_ID} underCheckbox><IntlMsg {...messages.captionAllowMissingDob} /></FieldCaption>

          {allowMissing && (
            <Box mt={2} ml={4} maxWidth="420px">
              <FieldCaption><IntlMsg {...messages.lblMissingDobFallback} /></FieldCaption>
              <Select
                inputId={`${FIELD_ID}-fallback`}
                aria-describedby={FALLBACK_CAPTION_ID}
                value={selectedOption}
                options={DATE_OF_BIRTH_FALLBACK_FORM_OPTIONS}
                onChange={handleSelectFallback}
              />
              <FieldCaption id={FALLBACK_CAPTION_ID}><IntlMsg {...fallbackCaptions[fallback]} /></FieldCaption>
            </Box>
          )}

          <Box my={2}><Divider /></Box>
          {/* Disabled rather than withheld: a control that disappears takes its state with it, and
              the operator has no way to see the override stopped applying. */}
          <FormControlLabel
            disabled={!canOverwrite}
            control={
              <Checkbox
                id={OVERWRITE_ID}
                checked={overwrite}
                disabled={!canOverwrite}
                onChange={handleToggleOverwrite}
                inputProps={{ 'aria-describedby': OVERWRITE_CAPTION_ID }}
              />
            }
            label={<IntlMsg {...messages.lblOverwriteDob} />}
          />
          <FieldCaption id={OVERWRITE_CAPTION_ID} underCheckbox>
            <IntlMsg {...(canOverwrite ? messages.captionOverwriteDob : messages.captionOverwriteDobUnavailable)} />
          </FieldCaption>
        </Box>
      </Paper>
    </Box>
  );
};
